import { Facebook, Instagram, MessageCircle, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n";

export default function Footer() {
  const { t } = useLang();

  const exploreLinks = [
    { key: "nav_why", href: "#why-us" },
    { key: "ft_l_plans", href: "#plans" },
    { key: "ft_l_process", href: "#process" },
    { key: "nav_about", href: "#about" },
    { key: "nav_contact", href: "#contact" },
  ];

  return (
    <footer className="border-t border-brand/15 bg-navy-800/60" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-12 h-12 rounded-full bg-white p-1.5 ring-1 ring-brand/50 shadow-[0_0_25px_rgba(99,102,241,0.25)] overflow-hidden">
                <img src="/logo.webp" alt="Early Appointments Solutions Inc. logo" className="w-full h-full object-contain" data-testid="footer-logo-img" />
              </span>
              <span className="leading-tight">
                <span className="block font-serif text-lg tracking-wide text-cream">Early Appointments</span>
                <span className="block text-[10px] uppercase tracking-[0.3em] text-brand/80">Solutions Inc.</span>
              </span>
            </div>
            <p className="mt-6 text-sm text-cream/50 leading-relaxed max-w-sm" data-testid="footer-tagline">
              {t("ft_tag")}
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-brand mb-6">{t("ft_explore")}</p>
            <div className="flex flex-col gap-3">
              {exploreLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`footer-link-${l.key}`}
                  className="text-sm text-cream/55 hover:text-brand transition-colors w-fit"
                >
                  {t(l.key)}
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-4">
            <p className="text-[10px] uppercase tracking-[0.25em] text-brand mb-6">{t("ft_contact")}</p>
            <div className="space-y-4">
              <a href="tel:+14039785035" data-testid="footer-phone" className="flex items-center gap-3 text-sm text-cream/55 hover:text-brand transition-colors w-fit">
                <Phone size={15} className="text-brand" /> +1 403 978 5035
              </a>
              <a href="mailto:earlyappointmentsinc@gmail.com" data-testid="footer-email" className="flex items-center gap-3 text-sm text-cream/55 hover:text-brand transition-colors w-fit break-all">
                <Mail size={15} className="text-brand flex-none" /> earlyappointmentsinc@gmail.com
              </a>
              <div className="flex gap-3 pt-2">
                {[
                  { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/profile.php?id=61591786393398", testid: "footer-social-facebook" },
                  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/early_appointments_solutions/", testid: "footer-social-instagram" },
                  { icon: MessageCircle, label: "WhatsApp", href: "https://wa.me/14039785035", testid: "footer-social-whatsapp" },
                  { icon: Mail, label: "Email", href: "mailto:earlyappointmentsinc@gmail.com", testid: "footer-social-email" },
                ].map(({ icon: Icon, label, href, testid }) => (
                  <a
                    key={label}
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={testid}
                    className="inline-flex items-center justify-center w-10 h-10 border border-brand/30 text-brand hover:bg-brand hover:text-white transition-colors duration-300"
                  >
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-brand/10 flex flex-col lg:flex-row justify-between gap-4">
          <p className="text-xs text-cream/35" data-testid="footer-copyright">
            {t("ft_rights")}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" data-testid="footer-link-privacy" className="text-xs text-cream/45 hover:text-brand transition-colors">
              {t("ft_l_privacy")}
            </Link>
            <Link to="/terms" data-testid="footer-link-terms" className="text-xs text-cream/45 hover:text-brand transition-colors">
              {t("ft_l_terms")}
            </Link>
          </div>
          <p className="text-xs text-cream/35 max-w-2xl leading-relaxed" data-testid="footer-disclaimer">
            {t("ft_disc")}
          </p>
          <p className="text-xs text-cream/45 leading-relaxed" data-testid="footer-quebec-note">
            {t("qc_note")}
          </p>
        </div>
      </div>
    </footer>
  );
}
